import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { App } from 'antd';
import paymentVoucherService, {
  PaymentVoucher,
  CreatePaymentVoucherPayload,
  PaymentVoucherFilters,
  OpenSupplierInvoiceForPayment,
  PaymentVoucherSettlementHistory,
} from '@/services/paymentVoucher.service';

export function usePaymentVouchers(filters?: PaymentVoucherFilters) {
  return useQuery({
    queryKey: ['payment-vouchers', filters],
    queryFn: () => paymentVoucherService.list(filters),
    staleTime: 30000,
  });
}

export function usePaymentVoucher(id: string | undefined) {
  return useQuery({
    queryKey: ['payment-vouchers', id],
    queryFn: () => paymentVoucherService.getById(id!),
    enabled: !!id,
    staleTime: 30000,
  });
}

export function useOpenSupplierInvoices(supplierId: string | undefined) {
  return useQuery({
    queryKey: ['open-supplier-invoices', supplierId],
    queryFn: () => paymentVoucherService.getOpenSupplierInvoices(supplierId!),
    enabled: !!supplierId,
    staleTime: 60000,
  });
}

export function useApSettlementContext(supplierId: string | undefined, currency?: string) {
  return useQuery({
    queryKey: ['ap-settlement-context', supplierId, currency],
    queryFn: () => paymentVoucherService.getApSettlementContext(supplierId!, currency),
    enabled: !!supplierId && !!currency,
    staleTime: 30000,
  });
}

export function useApSettlementContextMulti(
  supplierIds: string[] | undefined,
  currency?: string
) {
  const ids = (supplierIds ?? []).filter(Boolean);
  const key = [...ids].sort().join(',');
  return useQuery({
    queryKey: ['ap-settlement-context-multi', key, currency],
    queryFn: () => paymentVoucherService.getApSettlementContextMulti(ids, currency),
    enabled: ids.length > 0 && !!currency,
    staleTime: 30000,
  });
}

export function usePaymentVoucherSettlementHistory(id: string | undefined) {
  return useQuery({
    queryKey: ['payment-voucher-settlement-history', id],
    queryFn: () => paymentVoucherService.getSettlementHistory(id!),
    enabled: !!id,
    staleTime: 30000,
  });
}

export function usePaymentVoucherAdvanceAllocations(
  id: string | undefined,
  paymentType?: string
) {
  return useQuery({
    queryKey: ['payment-voucher-advance-allocations', id],
    queryFn: () => paymentVoucherService.getAdvanceAllocations(id!),
    enabled: !!id && paymentType === 'SupplierAdvance',
    staleTime: 30000,
  });
}

export function useCreatePaymentVoucher() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (payload: CreatePaymentVoucherPayload) => paymentVoucherService.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-vouchers'] });
      message.success('Payment voucher created');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || error?.message || 'Failed to create payment voucher');
    },
  });
}

export function useUpdatePaymentVoucher() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<CreatePaymentVoucherPayload> }) =>
      paymentVoucherService.update(id, payload),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['payment-vouchers'] });
      queryClient.invalidateQueries({ queryKey: ['payment-vouchers', id] });
      message.success('Payment voucher updated');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || error?.message || 'Failed to update payment voucher');
    },
  });
}

export function usePostPaymentVoucher() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (id: string) => paymentVoucherService.post(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['payment-vouchers'] });
      queryClient.invalidateQueries({ queryKey: ['payment-vouchers', id] });
      queryClient.invalidateQueries({ queryKey: ['supplier-invoices'] });
      message.success('Payment voucher posted');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || error?.message || 'Failed to post payment voucher');
    },
  });
}

export function useCancelPaymentVoucher() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (id: string) => paymentVoucherService.cancel(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['payment-vouchers'] });
      queryClient.invalidateQueries({ queryKey: ['payment-vouchers', id] });
      queryClient.invalidateQueries({ queryKey: ['supplier-invoices'] });
      message.success('Payment voucher cancelled');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || error?.message || 'Failed to cancel payment voucher');
    },
  });
}
